import { useState } from "react";
import { Trash2, Mail, Phone, CheckCircle2, AlertCircle } from "lucide-react";
import { adminApi } from "../lib/api";

const fmt = (iso) =>
    new Date(iso).toLocaleString("en-US", {
        timeZone: "America/Chicago",
        dateStyle: "medium",
        timeStyle: "short",
    }) + " CT";

function EmailStatus({ item }) {
    if (item.email_sent)
        return (
            <span className="inline-flex items-center gap-1.5 text-emerald-400">
                <CheckCircle2 size={13} /> Email delivered to inbox
            </span>
        );
    return (
        <span className="inline-flex items-center gap-1.5 text-red-400">
            <AlertCircle size={13} />
            {item.skipped_reason === "test_address" ? "Test — not emailed" : "Email failed"}
        </span>
    );
}

function Entry({ item }) {
    const body = item.source === "contact" ? item.message : item.notes;
    return (
        <li data-testid={`submission-${item.id}`} className="border border-white/10 bg-[#0A0A0A] p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="text-white font-semibold">{item.name}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5A623] border border-[#F5A623]/40 px-2 py-0.5">
                        {item.source === "contact" ? item.project_type || "Contact form" : "Project checklist"}
                    </span>
                </div>
                <span data-testid="submission-time" className="text-white/60 text-sm">{fmt(item.created_at)}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-5 text-sm text-white/70">
                <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1.5 hover:text-[#F5A623]"><Mail size={13} /> {item.email}</a>
                <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#F5A623]"><Phone size={13} /> {item.phone}</a>
                {item.company && <span>{item.company}</span>}
                {item.timeline && <span>Timeline: {item.timeline}</span>}
            </div>
            {body && <p className="mt-3 text-white/80 text-sm whitespace-pre-line">{body}</p>}
            {item.services?.length > 0 && (
                <p className="mt-3 text-white/60 text-xs">Services: {item.services.join(", ")}</p>
            )}
            <div className="mt-3 text-xs"><EmailStatus item={item} /></div>
        </li>
    );
}

export function SubmissionsLog({ items, onRefresh }) {
    const [busy, setBusy] = useState(false);
    const clearAll = async () => {
        if (!window.confirm(`Delete all ${items.length} submissions? This cannot be undone.`)) return;
        setBusy(true);
        await adminApi.delete("/admin/submissions");
        setBusy(false);
        onRefresh();
    };
    return (
        <section className="mt-10" data-testid="submissions-log">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-white/60 text-sm">Every time someone contacted Bevel through the website, newest first.</p>
                {items.length > 0 && (
                    <button onClick={clearAll} disabled={busy} data-testid="submissions-clear-all" className="btn-secondary !px-5 !py-3 !text-xs">
                        <Trash2 size={14} /> Clear all
                    </button>
                )}
            </div>
            {items.length === 0 ? (
                <p data-testid="submissions-empty" className="mt-8 text-white/50">No contacts yet.</p>
            ) : (
                <ul className="mt-6 space-y-4">{items.map((i) => <Entry key={i.id} item={i} />)}</ul>
            )}
        </section>
    );
}
